"use client"

import { useState } from "react"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent } from "@/components/ui/card"
import { Mail, Phone, MapPin, Clock, Send, MessageCircle, Users, Headphones, Linkedin } from "lucide-react"
import { useDemoModal } from "@/components/demo-modal-provider"

export default function ContactUsPage() {
  const { openDemoModal } = useDemoModal()
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    message: ""
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    try {
      const formDataObj = new FormData(e.currentTarget)
      const result = await submitContactForm({ success: false, message: '' }, formDataObj)

      if (result.success) {
        toast.success(result.message)
        setFormData({
          name: "",
          email: "",
          company: "",
          phone: "",
          subject: "",
          message: ""
        })
      } else {
        toast.error(result.message)
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#1a85ca] via-[#1570a0] to-[#34D1BE] text-white py-20 overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-10 right-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-10 left-10 w-24 h-24 bg-[#34D1BE]/30 rounded-full blur-lg"></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/5 rounded-full blur-md"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Get in <span className="text-[#34D1BE]">Touch</span>
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                Ready to transform your healthcare operations? Let's discuss how OctoHealth ERP can revolutionize your business.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm md:text-base">
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                  <MessageCircle className="h-4 w-4" />
                  <span>Quick Response</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                  <Users className="h-4 w-4" />
                  <span>Expert Team</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                  <Headphones className="h-4 w-4" />
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              
              {/* Contact Form */}
              <div>
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Send us a Message</h2>
                  <p className="text-lg text-gray-600">
                    Fill out the form below and our team will get back to you within 24 hours.
                  </p>
                </div>

                <Card className="shadow-xl border-0">
                  <CardContent className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                            Full Name *
                          </label>
                          <Input
                            id="name"
                            name="name"
                            required
                            placeholder="John Doe"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="h-12"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                            Work Email *
                          </label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            placeholder="john@company.com"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="h-12"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                            Company
                          </label>
                          <Input
                            id="company"
                            name="company"
                            placeholder="Your Company"
                            value={formData.company}
                            onChange={handleInputChange}
                            className="h-12"
                          />
                        </div>
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                            Phone Number
                          </label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            placeholder="+234 xxx xxx xxxx"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="h-12"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                          Subject
                        </label>
                        <Input
                          id="subject"
                          name="subject"
                          placeholder="How can we help you?"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className="h-12"
                        />
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                          Message *
                        </label>
                        <Textarea
                          id="message"
                          name="message"
                          required
                          placeholder="Tell us about your healthcare management needs..."
                          value={formData.message}
                          onChange={handleInputChange}
                          rows={6}
                          className="resize-none"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full h-12 bg-gradient-to-r from-[#1a85ca] to-[#34D1BE] hover:from-[#1570a0] hover:to-[#2bc4b2] text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Sending...</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <Send className="h-4 w-4" />
                            <span>Send Message</span>
                          </div>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Get in Touch</h2>
                  <p className="text-lg text-gray-600 mb-8">
                    We're here to help you transform your healthcare operations. Reach out through any of these channels.
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Email */}
                  <Card className="border-l-4 border-l-[#1a85ca] shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-[#1a85ca]/10 p-3 rounded-lg">
                          <Mail className="h-6 w-6 text-[#1a85ca]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">Email Us</h3>
                          <p className="text-gray-600 mb-2">Send us an email anytime</p>
                          <a href="mailto:sales@octosoft.ai" className="text-[#1a85ca] hover:underline font-medium">
                            sales@octosoft.ai
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Phone */}
                  <Card className="border-l-4 border-l-[#34D1BE] shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-[#34D1BE]/10 p-3 rounded-lg">
                          <Phone className="h-6 w-6 text-[#34D1BE]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">Call Us</h3>
                          <p className="text-gray-600 mb-2">Mon-Fri from 8am to 5pm</p>
                          <a href="tel:+23402013302766" className="text-[#34D1BE] hover:underline font-medium">
                            +234 (0) 201 330 2766
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Location */}
                  <Card className="border-l-4 border-l-purple-500 shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-purple-50 p-3 rounded-lg">
                          <MapPin className="h-6 w-6 text-purple-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">Visit Us</h3>
                          <p className="text-gray-600 mb-2">Come say hello at our office</p>
                          <p className="text-purple-500 font-medium">6th Floor, Mulliner Towers, 39 Alfred Rewane Road, Lagos Island, Lagos.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* LinkedIn */}
                  <Card className="border-l-4 border-l-[#0077b5] shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-[#0077b5]/10 p-3 rounded-lg">
                          <Linkedin className="h-6 w-6 text-[#0077b5]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">Connect on LinkedIn</h3>
                          <p className="text-gray-600 mb-2">Follow us for updates</p>
                          <a 
                            href="https://www.linkedin.com/company/octohealth-erp" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-[#0077b5] hover:underline font-medium"
                          >
                            OctoHealth ERP
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* CTA Section */}
                <Card className="bg-gradient-to-r from-[#1a85ca] to-[#34D1BE] text-white border-0">
                  <CardContent className="p-8 text-center">
                    <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
                    <p className="mb-6 text-white/90">
                      Schedule a personalized demo and see how OctoHealth ERP can transform your operations.
                    </p>
                    <Button 
                      className="bg-white text-[#1a85ca] hover:bg-gray-100 font-semibold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all"
                      onClick={openDemoModal}
                    >
                      Request Demo
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}